import '@/styles/beaverTheme.scss';
import { useEffect } from 'react';
import { observer, Provider } from 'mobx-react';
import { NextIntlClientProvider } from 'next-intl';
import { motion, AnimatePresence } from "framer-motion";
import { Router, useRouter } from "next/router";
import { SpeedInsights } from "@vercel/speed-insights/next";
import Head from 'next/head';
import Script from 'next/script';
import NProgress from 'nprogress';
import { SessionProvider } from 'next-auth/react';
import wooCommerceStore from '../stores/woocommerceStore';
import generalSettingsStore from '../stores/generalSettingsStore';
import studioStore from '../stores/studioStore';
import favoritesStore from '../stores/favoritesStore';
import { replaceBackendWithFrontendUrl } from "@/utils/misc";
import { getYoastHead } from "@/utils/yoastSeo";
import fontData from "@/utils/fontData";
import MinOrderModal from "@/components/modals/MinOrderModal";
import ShippingAreaCheckModal from "@/components/modals/ShippingAreaCheckModal";
import CartSyncModal from "@/components/modals/CartSyncModal";
import QuantityError from '@/components/cart/QuantityError';
import { hexToRgba, rgbaToHex } from '@/utils/misc';

const stores = {
  wooCommerceStore,
  generalSettingsStore,
  studioStore,
  favoritesStore,
};

const TrackingCodes = observer(() => {
  const { tracking } = generalSettingsStore.settings;

  if (!tracking) return null;

  return (
    <>
      {/* Facebook Pixel */}
      {tracking.facebook_pixel?.enabled && tracking.facebook_pixel?.id && (
        <Script id="facebook-pixel" strategy="afterInteractive">
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${tracking.facebook_pixel.id}');
          `}
        </Script>
      )}

      {/* Google Analytics */}
      {tracking.google_analytics?.enabled && tracking.google_analytics?.id && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${tracking.google_analytics.id}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${tracking.google_analytics.id}');
            `}
          </Script>
        </>
      )}

      {/* Google Ads */}
      {tracking.google_ads?.enabled && tracking.google_ads?.id && (
        <Script
          src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${tracking.google_ads.id}`}
          strategy="afterInteractive"
          crossOrigin="anonymous"
        />
      )}
    </>
  );
});

const App = observer(({ Component, pageProps }) => {
  // Reset favorites store when user logs out
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.storageArea === localStorage && e.key === 'token' && !e.newValue) {
        // User logged out, reset favorites store
        favoritesStore.resetInitialization();
        favoritesStore.setFavorites([]);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const loadFonts = async () => {
      const fontTypes = ['primary', 'secondary', 'special'];
      const loadedFonts = {};

      for (const type of fontTypes) {
        const fontName = studioStore[`get${type.charAt(0).toUpperCase() + type.slice(1)}Font`]();
        const normalizedFontName = fontName.replace(/_/g, ' ').toLowerCase();

        const fontEntry = Object.entries(fontData).find(([key]) =>
          key.replace(/_/g, ' ').toLowerCase() === normalizedFontName,
        );

        if (fontEntry) {
          const [fontName, fontVariants] = fontEntry;
          try {
            const fontFaces = fontVariants.flatMap(({ sources, unicodeRange }) =>
              sources.map(({ url, format }) =>
                new FontFace(fontName, `url(${url}) format('${format}')`, { unicodeRange }),
              ),
            );

            const loadedFontFaces = await Promise.all(fontFaces.map(font => font.load()));
            loadedFontFaces.forEach(font => document.fonts.add(font));
            loadedFonts[type] = fontName;
          } catch (error) {
            console.error(`Failed to load ${type} font:`, error);
          }
        } else {
          console.warn(`Font data not found for ${type} font: ${fontName}`);
        }
      }

      // Set CSS variables for fonts
      document.documentElement.style.setProperty('--primary-font', loadedFonts.primary || 'inherit');
      document.documentElement.style.setProperty('--secondary-font', loadedFonts.secondary || 'inherit');
      document.documentElement.style.setProperty('--special-font', loadedFonts.special || 'inherit');
      // Colors
      document.documentElement.style.setProperty('--primary-color', studioStore?.colors?.primary_color || 'inherit');
      document.documentElement.style.setProperty('--primary-light-color', rgbaToHex(hexToRgba(studioStore?.colors?.primary_color, 0.2, 'object'), 'rgb') || 'inherit');
      document.documentElement.style.setProperty('--primary-light-color-hover', rgbaToHex(hexToRgba(studioStore?.colors?.primary_color, 0.4, 'object'), 'rgb') || 'inherit');
      document.documentElement.style.setProperty('--primary-text-color', studioStore?.colors?.primary_text_color || 'inherit');
      document.documentElement.style.setProperty('--secondary-color', studioStore?.colors?.secondary_color || 'inherit');
      document.documentElement.style.setProperty('--secondary-light-color', rgbaToHex(hexToRgba(studioStore?.colors?.secondary_color, 0.2, 'object'), 'rgb') || 'inherit');
      document.documentElement.style.setProperty('--secondary-light-color-hover', rgbaToHex(hexToRgba(studioStore?.colors?.secondary_color, 0.4, 'object'), 'rgb') || 'inherit');
      document.documentElement.style.setProperty('--secondary-text-color', studioStore?.colors?.secondary_text_color || 'inherit');
      document.documentElement.style.setProperty('--success-color', studioStore?.colors?.success_color || 'inherit');
      document.documentElement.style.setProperty('--warning-color', studioStore?.colors?.warning_color || 'inherit');
      document.documentElement.style.setProperty('--error-color', studioStore?.colors?.error_color || 'inherit');
      // Backgrounds
      document.documentElement.style.setProperty('--main-menu-background-color', studioStore?.settings?.header?.main_menu?.background_color || 'inherit');
      document.documentElement.style.setProperty('--products-background-color', studioStore?.settings?.category_page?.background_color || 'inherit');
      document.documentElement.style.setProperty('--login-register-background-color', studioStore?.settings?.login_register?.background_color || 'inherit');
    };

    loadFonts();
  }, []);

  NProgress.configure({ showSpinner: false });
  Router.events.on('routeChangeStart', () => NProgress.start());
  Router.events.on('routeChangeComplete', () => NProgress.done());
  Router.events.on('routeChangeError', () => NProgress.done());

  const variants = {
    inactive: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    in: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    },
    out: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    },
  };

  const { asPath } = useRouter();
  let lang = 'en';
  let timezone = '';

  if (pageProps.data?.woocommerce?.products) {
    wooCommerceStore.setProductSettings(pageProps.data?.woocommerce?.products);
  }

  if (pageProps.data?.woocommerce?.general) {
    wooCommerceStore.setGeneralSettings(pageProps.data?.woocommerce?.general);
  }

  if (pageProps.studioData) {
    studioStore.setSettings(pageProps.studioData);
  }

  if (pageProps.data) {
    generalSettingsStore.setSettings(replaceBackendWithFrontendUrl(pageProps.data));
  }

  if (pageProps.data?.language) {
    lang = pageProps.data.language.split('_')[0];
  }

  if (pageProps.data?.timezone) {
    timezone = pageProps.data.timezone;
  }

  const onExit = () => {
    NProgress.done();
  }

  return (
    <SessionProvider session={pageProps.session}>
      <Head>
        {pageProps.pageData && getYoastHead(pageProps.pageData)}
      </Head>
      <NextIntlClientProvider
        locale={lang}
        messages={pageProps.messages}
        timeZone={timezone}
        onError={(err) => { if (err.code !== 'MISSING_MESSAGE' && err.code !== 'ENVIRONMENT_FALLBACK') console.error(err); }}
        getMessageFallback={({ key }) => key}
      >
        <Provider {...stores}>
          <SpeedInsights />
          <AnimatePresence
            initial={false}
            mode="wait"
            onExitComplete={() => onExit()}
          >
            <motion.div
              className={`font-condensed main-app`}
              style={{
                fontFamily: 'var(--primary-font, inherit)',
              }}
              key={asPath}
              variants={variants}
              initial="in"
              animate="inactive"
              exit="out"
              transition={{ type: 'tween' }}
            >
              <TrackingCodes />
              <Component {...pageProps} />
              <QuantityError />
              {wooCommerceStore.isMinOrderModalOpen && <MinOrderModal />}
              {wooCommerceStore.isShippingAreaCheckModalOpen && <ShippingAreaCheckModal />}
              {wooCommerceStore.isCartSyncModalOpen && <CartSyncModal />}
            </motion.div>
          </AnimatePresence>
        </Provider>
      </NextIntlClientProvider>
    </SessionProvider>
  );
});

export default App;